// WebGL map engine (MapLibre GL) for the planning map — the renderer behind FlightPlanning.
//
// FlightPlanning talks ONLY to a renderer-agnostic engine (this, or a future LeafletEngine), so the
// panels/search/table/detail card are reused verbatim across renderers. This engine owns map creation +
// the full layer stack, data (setZoneData/setFlightData/…), filtering/visibility, view (fly/fit), 3D/
// terrain/weather, and interaction (hover/click/rotate/keyboard) — delegating tooltip CONTENT + ACTIONS
// to the panel via MapInteractionHost. No MapLibre type leaks across the seam.
import maplibregl from 'maplibre-gl';
import mlcontour from 'maplibre-contour';
import type { ExpressionSpecification } from 'maplibre-gl';
import { categoryIcon, CATEGORIES } from '../legend.ts';
import { t } from '../../i18n.svelte.ts';
import type { Basemap, MountOpts, MapBounds, TipRef, MapInteractionHost, PlanningMapEngine, FlightPath } from './engine.ts';
import { FlightDeck } from './flightDeck.ts';
import { streetsLayers, STREETS_SOURCE, STREETS_ATTRIBUTION, STREETS_LAYER_IDS } from './streetsBasemap.ts';
import { satelliteRaster } from './rasterBasemaps.ts';
import { plog, dumpResourceTimings } from '../../perf.ts';

// Contour lines are generated from the DEM client-side by maplibre-contour. The DEM source + its
// MapLibre protocol are registered ONCE per module load (addProtocol throws on a second registration).
// `worker: false` keeps it main-thread — no Vite worker bundling.
const demSource = new mlcontour.DemSource({
  url: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
  encoding: 'terrarium',
  maxzoom: 13,
  worker: false,
});
let contourProtocolReady = false;
function ensureContourProtocol(): typeof demSource {
  if (!contourProtocolReady) {
    demSource.setupMaplibre(maplibregl);
    contourProtocolReady = true;
  }
  return demSource;
}

// Heliport isn't a zone category (it's a basemap aerodrome class) but shares the same emoji-icon scheme.
const HELIPORT_ICON = '🚁';
// Continuous cold→hot ramp for the temperature field (same anchors as the marker chip, interpolated).
const TEMP_RAMP: ExpressionSpecification = [
  'interpolate', ['linear'], ['get', 'temp'],
  -10, '#4338ca', 0, '#2563eb', 10, '#22d3ee', 18, '#22c55e', 25, '#eab308', 32, '#f97316', 40, '#ef4444',
];

/** All capabilities are supported by the WebGL renderer. */
/** Satellite imagery, from whichever Esri door this build is licensed for (see rasterBasemaps.ts). */
const SAT = satelliteRaster();
const SAT_SOURCE = { type: 'raster' as const, tiles: [SAT.url], tileSize: SAT.tileSize, maxzoom: SAT.maxZoom, attribution: SAT.attribution };

export const MAPLIBRE_CAPABILITIES = { threeD: true, terrain: true, weather: true, effectiveZones: true, vectorLabels: true } as const;

const ZONE_LAYERS = ['zones-nofly-fill', 'zones-ok-fill', 'notam-fill'];
const SYMBOL_LAYERS = ['poi-aero', 'poi-zone']; // basemap aerodromes + drone-relevant zone-centre markers

/**
 * Zoom below which zone-centre markers draw their ICON but not their NAME.
 *
 * Only the text is gated, never the icon. The icons already declutter themselves — the layer sets
 * `icon-allow-overlap: false`, so MapLibre drops colliding markers and what survives still shows
 * WHERE the restricted sites are. A layer `minzoom` would throw that away and break scanning a
 * province for military areas, which is a real way to use this map.
 *
 * Names are different: at country zoom a label like "GEREDE ALLAR AKARYAKIT İSTASYONU-YASAK BÖLGE"
 * is unreadable at 10px and, in sparse regions, does not collide with anything — so `text-optional`
 * never drops it and it just sits there as noise. The floor removes exactly that case.
 *
 * The user's Names toggle still wins: with names off there is no text at any zoom.
 */
const POI_NAME_MIN_ZOOM = 8.5;

/** `text-field` for the zone POI layer: the name, but only once names are legible. */
const poiNameField = (): ExpressionSpecification =>
  ['step', ['zoom'], '', POI_NAME_MIN_ZOOM, ['get', 'name']] as ExpressionSpecification;
// The effective-mode rescue ring is a LOW-ZOOM safety net, not a style: a genuinely tiny red pocket
// (≲1.1 km) sub-pixels away inside a big allowed area at overview zoom. It must trigger on ON-SCREEN size,
// so it's gated two ways — only tiny zones (`SMALL_NOFLY`), and only while zoomed out (`EFF_RING_OPACITY`
// fades it to nothing by z12). Above that the fill is already an unmissable blob, so ringing it would just
// make an ordinary zone look like a special category. A geographic-size filter alone can't do this (it's
// zoom-blind → rings a 1 km zone even at z14 where it's huge).
const SMALL_NOFLY = ['<', ['get', 'maxext'], 0.01] as unknown as ExpressionSpecification;
const EFF_RING_OPACITY = ['interpolate', ['linear'], ['zoom'], 10, 1, 12, 0] as unknown as ExpressionSpecification;
// Dark casing + red line widths, eased slightly wider when zoomed out (where on-screen pockets are
// smallest). The stroke is measured in PIXELS, so a sub-pixel fill still leaves a legible red ring.
const NOFLY_CASE_W = ['interpolate', ['linear'], ['zoom'], 6, 4.5, 12, 4] as unknown as ExpressionSpecification;
const NOFLY_LINE_W = ['interpolate', ['linear'], ['zoom'], 6, 2.6, 12, 2.2] as unknown as ExpressionSpecification;

export class MapLibreEngine implements PlanningMapEngine {
  private map: maplibregl.Map | undefined;
  private is3d = false; // 3D presentation state, owned by the pitch watcher (see on3DChange)
  private deck: FlightDeck | undefined; // clean 3D airborne routes + curtain (deck.gl overlay, lazy)
  private flOn = false; // "show my flights" is on
  private flAir = false; // the airborne-route (3D) toggle is on
  private flCurtain = false; // the 3D altitude-curtain toggle is on
  readonly capabilities = MAPLIBRE_CAPABILITIES;
  private readyCbs: Array<() => void> = [];
  private ready = false;
  private windMarkers: maplibregl.Marker[] = [];
  private wxPopup: maplibregl.Popup | undefined;
  // Interaction state (hover coalescing, popup, assess pin, rotate/keyboard) — owned by the engine.
  private hoverPopup: maplibregl.Popup | undefined;
  /**
   * The tap-to-identify popup, separate from `hoverPopup` because only this one carries a close
   * button — an X on every desktop hover tooltip would be noise, and a touch popup with no way to
   * dismiss it is a trap. The two never coexist: MapLibre raises `mousemove` for pointing devices,
   * not for touch, so on a phone `hoverPopup` is never shown at all.
   */
  private tapPopup: maplibregl.Popup | undefined;
  /** What raised the pending click: a finger, or a mouse/pen. Set on pointerdown, read on click. */
  private lastPointerType = '';
  private assessMarker: maplibregl.Marker | undefined;
  private hoverLngLat: maplibregl.LngLat | undefined;
  private hoverEv: maplibregl.MapMouseEvent | null = null;
  private hoverRaf = 0;
  private lastHx = -1;
  private lastHy = -1;
  private dragging = false;
  private clickTimer: ReturnType<typeof setTimeout> | undefined;
  // Every other deferred callback the engine schedules, so destroy() can cancel them. clickTimer was
  // the only one being cleared; the terrain re-anchor chain (up to 8 retries, 300 ms apart) and the
  // two resource-timing dumps kept firing after teardown, and anchor() reached into a FlightDeck that
  // dispose() had already released. Use later() for anything deferred that touches engine state.
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private later(fn: () => void, ms: number): void {
    const id = setTimeout(() => { this.timers.delete(id); fn(); }, ms);
    this.timers.add(id);
  }
  private interactionCleanup: (() => void) | undefined;


  /** Create the map + controls and build the (data-empty) layer stack. Throws synchronously if WebGL is
   *  unavailable (MapLibre's constructor throws) — the caller catches that to trigger the fallback. */
  mount(container: HTMLElement, opts: MountOpts): void {
    const sat0 = opts.basemap === 'satellite';
    const t0 = plog('engine.mount() start'); // phase clock: construction → style load → first tile render
    ensureContourProtocol(); // register the maplibre-contour protocol before the style references it
    this.map = new maplibregl.Map({
      container,
      // MapLibre ships its own English strings for the controls it injects, and they are the only
      // English left on a page that is otherwise entirely Turkish — including the one control a
      // visitor on a phone reaches for first. These are also the accessible names, so a screen
      // reader announced "Find my location" in the middle of Turkish.
      locale: {
        'NavigationControl.ZoomIn': t('Zoom in'),
        'NavigationControl.ZoomOut': t('Zoom out'),
        'NavigationControl.ResetBearing': t('Reset bearing to north'),
        'GeolocateControl.FindMyLocation': t('Find my location'),
        'GeolocateControl.LocationNotAvailable': t('Location not available'),
        'AttributionControl.ToggleAttribution': t('Toggle attribution'),
        'Popup.Close': t('Close popup'),
        'Map.Title': t('Map'),
      },
      style: {
        version: 8,
        // Vector glyphs for the (rotation-readable) labels. NO sprite: every icon on this map is a canvas
        // image registered via addImage (emoji category icons, the photo marker) — no layer references a
        // sprite-sheet name, so declaring a `sprite` URL only added a blocking JSON+PNG fetch to style load
        // (measured ~part of the 3 s open) for images we never use.
        glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
        sources: {
          // Esri World Imagery has real tiles only to ~z18 over rural Turkey; cap at 18 so MapLibre
          // OVERZOOMS the last real tile instead of showing its "not yet available" placeholder.
          sat: SAT_SOURCE,
          // maxzoom 12 (not 14): the 3D terrain mesh is rebuilt from these DEM tiles, and z14 loaded ~16× more
          // tiles → a ~1 s main-thread stall on entering 3D (measured). z12 (~38 m/px) keeps the relief shape
          // while cutting the mesh/tile work; MapLibre overzooms past z12 for the fine detail we don't need here.
          dem: { type: 'raster-dem', tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'], encoding: 'terrarium', tileSize: 256, maxzoom: 12, attribution: 'Elevation: Mapzen / Terrarium' },
          [STREETS_SOURCE]: { type: 'vector', url: 'https://tiles.openfreemap.org/planet', attribution: STREETS_ATTRIBUTION },
        },
        // The streets basemap is declared here rather than added on 'load', because it IS the ground:
        // adding it later would show the dark background first and repaint the whole map underneath
        // the overlays. Satellite stays a single raster layer on top of that background.
        layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': '#2b2f38' } },
          ...streetsLayers(!sat0),
          { id: 'sat', type: 'raster', source: 'sat', layout: { visibility: sat0 ? 'visible' : 'none' } },
        ],
      },
      center: opts.center,
      zoom: opts.zoom,
      pitch: opts.pitch ?? 0, // restored from the last view (a >0 pitch flips 3D on via the pitch watcher)
      bearing: opts.bearing ?? 0,
      maxZoom: 19, // keep overzoom mild (sat imagery is real to z18) so it stays legible, not a blur
      attributionControl: { compact: true },
    });
    this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    const geolocate = new maplibregl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: false, showAccuracyCircle: true });
    // Without this the control fails silently: it greys its own button out and changes a `title` no
    // touch device will ever show. "Where am I relative to that red area" is the question this map
    // exists to answer on a phone, so a refusal has to say what happened and what to do about it —
    // and the three causes need three different answers (denied / unavailable / timed out), not one
    // message that sends a user who revoked permission off to look for open sky.
    geolocate.on('error', (e: GeolocationPositionError) => {
      const msg =
        e?.code === 1 ? t('Location permission is denied. Allow it for this site in your browser settings, then try again.')
        : e?.code === 3 ? t('Locating timed out. Try again — outdoors it is usually faster.')
        : t('Your location could not be determined. GPS may be off, or there may be no signal indoors.');
      this.notice(msg);
    });
    this.map.addControl(geolocate, 'bottom-right');
    this.map.on('error', (e) => console.warn('[MapLibreEngine]', e?.error?.message ?? e));
    // Fine-grained load breakdown so we optimise the 3 s open from evidence, not guesses. Phases off t0:
    //   styledata      → style JSON parsed (+ sprite/glyph metadata)
    //   basemap tiles  → the ACTIVE basemap source finished loading its first tiles (what you actually see)
    //   first render   → first paint to the canvas
    //   'load'         → MapLibre's "first complete render" (this is what gated `ready`, ~3 s)
    // ('idle' was removed as a signal — with weather/terrain it may not fire for minutes and read as 163 s.)
    const activeSrc = sat0 ? 'sat' : STREETS_SOURCE;
    this.map.once('render', () => plog('map: first render (first paint)', t0));
    const onSrc = (e: maplibregl.MapSourceDataEvent): void => {
      if (e.sourceId === activeSrc && e.isSourceLoaded) {
        plog(`map: basemap "${activeSrc}" tiles loaded`, t0);
        this.map?.off('sourcedata', onSrc);
      }
    };
    this.map.on('sourcedata', onSrc);
    // Build the layer stack on 'load'. (An earlier attempt to build sooner via isStyleLoaded()/styledata was
    // MEASURED to never fire before 'load' — isStyleLoaded stayed false until the load event, so it just fell
    // through to this same path. Building the zones/overlays genuinely earlier would mean declaring their
    // sources+layers in the INITIAL style object, not addLayer-after-load — a bigger change held pending the
    // resource-timing data that says whether 'load' is gated by the satellite tiles or something else.)
    this.map.on('load', () => {
      const m = this.map;
      if (!m || this.ready) return;
      const tb = plog('map: building layers (on load)', t0);
      this.buildLayers(m);
      plog('map: buildLayers() done', tb);
      this.ready = true;
      for (const cb of this.readyCbs) cb();
    });
    // Plain observation (NOT a "loaded" claim): if/when the map goes idle. Earlier this read 163 s, which
    // told us the map keeps loading tiles long after it's usable — the resource dumps below show what.
    this.map.once('idle', () => plog('map: idle (all tiles settled)', t0));
    // The actual network waterfall — measured, not inferred. Two snapshots: ~2.5 s captures the initial load
    // (sprite/glyphs/TileJSON/first tiles), ~7 s captures everything that streamed in after (the 163 s-idle
    // tail). These replace the guesswork about which fetch dominates.
    this.later(() => dumpResourceTimings('resources @2.5s (initial load)'), 2500);
    this.later(() => dumpResourceTimings('resources @7s (settled)'), 7000);
  }

  /** Fires once the style has loaded and every layer exists (immediately if already ready). */
  onReady(cb: () => void): void {
    if (this.ready) cb();
    else this.readyCbs.push(cb);
  }

  setBasemap(kind: Basemap): void {
    const m = this.map;
    if (!m) return;
    const sat = kind === 'satellite';
    // The streets basemap is a STACK, not one layer. Driving it from the exported id list is what
    // keeps a layer added to the style from being silently left visible under the satellite.
    for (const id of STREETS_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', sat ? 'none' : 'visible');
    }
    if (m.getLayer('sat')) m.setLayoutProperty('sat', 'visibility', sat ? 'visible' : 'none');
  }

  // ---- data (the panel builds the FeatureCollections from its state; the engine just renders them) ----
  private src(id: string): maplibregl.GeoJSONSource | undefined {
    return this.map?.getSource(id) as maplibregl.GeoJSONSource | undefined;
  }
  private static readonly EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
  private static point(lnglat: [number, number]): GeoJSON.Feature {
    return { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: lnglat } };
  }
  /** A tiny fill-extrusion "bead" floating at `alt` (m rel_alt) — the marker AT the airborne path, so in a
   *  pitched/3D view it rides the flight, not the ground track. `alt=0` sits it on the ground (a takeoff). */
  private static bead(lnglat: [number, number], alt: number): GeoJSON.FeatureCollection {
    const half = 5 / 111_000; // ~5 m square, in degrees
    const [x, y] = lnglat;
    const ring = [[x - half, y - half], [x + half, y - half], [x + half, y + half], [x - half, y + half], [x - half, y - half]];
    return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: { base: Math.max(0, alt - 5), height: alt + 5 }, geometry: { type: 'Polygon', coordinates: [ring] } }] };
  }
  setZoneData(nofly: GeoJSON.FeatureCollection, ok: GeoJSON.FeatureCollection): void {
    this.src('zones-nofly')?.setData(nofly);
    this.src('zones-ok')?.setData(ok);
  }
  setNotamData(fc: GeoJSON.FeatureCollection): void { this.src('notams')?.setData(fc); }
  setPoiData(fc: GeoJSON.FeatureCollection): void { this.src('poi-zone')?.setData(fc); }
  setEffectiveData(nofly: GeoJSON.FeatureCollection, notam: GeoJSON.FeatureCollection, ok: GeoJSON.FeatureCollection): void {
    this.src('eff-nofly')?.setData(nofly);
    this.src('eff-notam')?.setData(notam);
    this.src('eff-ok')?.setData(ok);
  }
  setFlightData(track: GeoJSON.FeatureCollection, dots: GeoJSON.FeatureCollection): void {
    // Ground track + start dots only. The airborne route + altitude curtain are the deck.gl overlay
    // (setFlightPaths → FlightDeck); there is no MapLibre fill-extrusion flight layer anymore.
    this.src('flights')?.setData(track);
    this.src('flights-dots')?.setData(dots);
  }
  setFlightPaths(paths: FlightPath[]): void { this.deck?.setPaths(paths); }
  setPhotoData(photos: GeoJSON.FeatureCollection): void {
    this.src('photos')?.setData(photos);
  }
  /** Outline geometry under the cursor (zone/flight/merged block), or clear it. */
  setHighlight(feature: GeoJSON.Feature | null): void { this.src('highlight')?.setData(feature ?? MapLibreEngine.EMPTY_FC); }
  /** The point hovered ALONG a flight path (telemetry + frame). Ground circle (shadow) + a bead at `alt`. */
  setPathPoint(lnglat: [number, number] | null, alt = 0): void {
    this.src('path-point')?.setData(lnglat ? MapLibreEngine.point(lnglat) : MapLibreEngine.EMPTY_FC);
    this.src('path-point-air')?.setData(lnglat ? MapLibreEngine.bead(lnglat, alt) : MapLibreEngine.EMPTY_FC);
  }
  /** The moving playback dot (video ↔ map sync). Ground circle + a bead floating at the flight's altitude. */
  setPlaybackMarker(lnglat: [number, number] | null, alt = 0): void {
    this.src('play-point')?.setData(lnglat ? MapLibreEngine.point(lnglat) : MapLibreEngine.EMPTY_FC);
    this.src('play-point-air')?.setData(lnglat ? MapLibreEngine.bead(lnglat, alt) : MapLibreEngine.EMPTY_FC);
  }

  // ---- visibility / filtering (the panel decides WHAT is shown; the engine maps it to layers) ----
  private vis(id: string, on: boolean): void {
    const m = this.map;
    if (m?.getLayer(id)) m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  }
  /** Direct mode shows the raw semi-transparent zones; effective mode shows the merged blocks instead. */
  setZoneVisibility(nofly: boolean, ok: boolean, notam: boolean, effective: boolean): void {
    this.vis('zones-nofly-fill', nofly && !effective); this.vis('zones-nofly-hatch', nofly && !effective); this.vis('zones-nofly-casing', nofly && !effective); this.vis('zones-nofly-line', nofly && !effective);
    this.vis('zones-ok-fill', ok && !effective); this.vis('zones-ok-line', ok && !effective);
    this.vis('notam-fill', notam && !effective); this.vis('notam-line', notam && !effective);
    // Effective fills only — the smallest-wins footprints aren't unioned, so large-zone outlines would seam;
    // the eff-nofly ring is filtered to SMALL pockets, so it's safe to show (keeps tiny red carve-outs visible).
    this.vis('eff-nofly-fill', nofly && effective); this.vis('eff-nofly-hatch', nofly && effective); this.vis('eff-nofly-casing', nofly && effective); this.vis('eff-nofly-line', nofly && effective);
    this.vis('eff-ok-fill', ok && effective); this.vis('eff-ok-line', false);
    this.vis('eff-notam-fill', notam && effective); this.vis('eff-notam-line', false);
  }
  setTerrainLayers(hillshade: boolean, elevation: boolean, contours: boolean): void {
    this.vis('hillshade', hillshade);
    this.vis('color-relief', elevation);
    for (const l of ['contour-casing', 'contour-lines', 'contour-labels']) this.vis(l, contours);
  }
  setPoi(on: boolean, symbols: boolean, names: boolean, categories: string[]): void {
    const m = this.map;
    if (!m) return;
    this.vis('poi-aero', on);
    this.vis('poi-zone', on);
    if (m.getLayer('poi-zone')) {
      m.setFilter('poi-zone', ['in', ['get', 'category'], ['literal', categories]] as ExpressionSpecification);
      // Icons and names toggle independently: '' hides that part AND drops its collision box.
      m.setLayoutProperty('poi-zone', 'icon-image', symbols ? (['get', 'sym'] as ExpressionSpecification) : '');
      m.setLayoutProperty('poi-zone', 'text-field', names ? poiNameField() : '');
    }
  }
  // The panel's il/ilçe/köy checkboxes arrive as the union of their OMT classes; map each bucket layer's
  // visibility to whether any of its classes are selected. Country/continent (lbl-place-base) stay on always.
  setPlaceLabels(classes: string[]): void {
    const has = (...cs: string[]) => cs.some((c) => classes.includes(c));
    this.vis('lbl-place-il', has('city', 'state'));
    this.vis('lbl-place-ilce', has('town'));
    this.vis('lbl-place-koy', has('village', 'hamlet', 'isolated_dwelling', 'suburb', 'quarter', 'neighbourhood'));
  }
  setFlightVisibility(on: boolean, air: boolean, ground: boolean, curtain: boolean): void {
    // The dot + label anchor the flight even when the ground line is hidden, so keep them with EITHER path.
    for (const l of ['flights-casing', 'flights-line']) this.vis(l, on && ground);
    for (const l of ['flights-dot', 'flights-label']) this.vis(l, on && (ground || air));
    this.vis('photos-dot', on); // photos ride the same "show my captures" toggle
    this.flOn = on;
    this.flAir = air;
    this.flCurtain = curtain;
    this.updateDeck();
  }
  // The deck route + curtain show only in a pitched (3D) view AND when flights are on; top-down they'd just
  // overlap the ground track. Driven from both the pitch watcher and the airborne/curtain toggles.
  private updateDeck(): void {
    const on3d = this.is3d && this.flOn;
    void this.deck?.setVisibility(on3d && this.flAir, on3d && this.flCurtain);
  }
  /** When a flight's detail card is open, that flight stays bright and the others fade back. */
  setFlightFocus(fid: string | null): void {
    const m = this.map;
    if (!m) return;
    const lineOp = fid ? (['case', ['==', ['get', 'id'], fid], 1, 0.12] as ExpressionSpecification) : 0.95;
    const txtOp = fid ? (['case', ['==', ['get', 'id'], fid], 1, 0.15] as ExpressionSpecification) : 1;
    if (m.getLayer('flights-line')) m.setPaintProperty('flights-line', 'line-opacity', lineOp);
    if (m.getLayer('flights-label')) m.setPaintProperty('flights-label', 'text-opacity', txtOp);
  }
  /** Faceted sub-filters: restrict zones to selected authorities AND categories, NOTAMs to types. The
   *  SAME facets filter the effective footprints (tagged) — no recompute, just show/hide. */
  setZoneFacets(authorities: number[], noflyCats: string[], okCats: string[], notamTypes: string[]): void {
    const m = this.map;
    if (!m) return;
    const authClause = ['in', ['get', 'authority'], ['literal', authorities]] as ExpressionSpecification;
    const kindFilter = (selCats: string[]) => ['all', authClause, ['in', ['get', 'category'], ['literal', selCats]]] as unknown as ExpressionSpecification;
    for (const l of ['zones-nofly-fill', 'zones-nofly-hatch', 'zones-nofly-casing', 'zones-nofly-line']) if (m.getLayer(l)) m.setFilter(l, kindFilter(noflyCats));
    for (const l of ['zones-ok-fill', 'zones-ok-line']) if (m.getLayer(l)) m.setFilter(l, kindFilter(okCats));
    const notamFilter = ['in', ['get', 'atype'], ['literal', notamTypes]] as ExpressionSpecification;
    for (const l of ['notam-fill', 'notam-line']) if (m.getLayer(l)) m.setFilter(l, notamFilter);
    for (const l of ['eff-nofly-fill', 'eff-nofly-hatch']) if (m.getLayer(l)) m.setFilter(l, kindFilter(noflyCats));
    // The eff ring stays restricted to small pockets AND respects the category facet.
    const effRing = ['all', SMALL_NOFLY, kindFilter(noflyCats)] as unknown as ExpressionSpecification;
    for (const l of ['eff-nofly-casing', 'eff-nofly-line']) if (m.getLayer(l)) m.setFilter(l, effRing);
    if (m.getLayer('eff-ok-fill')) m.setFilter('eff-ok-fill', kindFilter(okCats));
    if (m.getLayer('eff-notam-fill')) m.setFilter('eff-notam-fill', ['in', ['get', 'ntype'], ['literal', notamTypes]] as ExpressionSpecification);
  }

  resize(): void {
    this.map?.resize();
  }

  // ---- view ----
  flyTo(center: [number, number], zoom: number, opts?: { duration?: number; curve?: number; speed?: number }): void {
    // Only forward the keys the caller actually set. maplibre merges the options object OVER its own
    // defaults with a plain copy, so an explicit `curve: undefined` REPLACES the default 1.42 instead of
    // falling back to it — the flight-path maths then divides by undefined and the ease throws
    // "Cannot read properties of null" from _calcMatrices mid-animation. Spreading conditionally keeps a
    // no-options `flyTo(center, zoom)` on maplibre's defaults, which is what every caller expects.
    this.map?.flyTo({
      center,
      zoom,
      ...(opts?.duration === undefined ? {} : { duration: opts.duration }),
      ...(opts?.curve === undefined ? {} : { curve: opts.curve }),
      ...(opts?.speed === undefined ? {} : { speed: opts.speed }),
      essential: true,
    });
  }
  fitBounds(bounds: [[number, number], [number, number]], opts?: { padding?: number; duration?: number; maxZoom?: number }): void {
    const o: maplibregl.FitBoundsOptions = { essential: true };
    if (opts?.padding != null) o.padding = opts.padding;
    if (opts?.duration != null) o.duration = opts.duration;
    if (opts?.maxZoom != null) o.maxZoom = opts.maxZoom;
    this.map?.fitBounds(bounds, o);
  }
  getZoom(): number { return this.map?.getZoom() ?? 0; }
  getCenter(): [number, number] { const c = this.map?.getCenter(); return c ? [c.lng, c.lat] : [0, 0]; }
  getViewState(): { center: [number, number]; zoom: number; pitch: number; bearing: number } {
    const m = this.map;
    const c = m?.getCenter();
    return { center: c ? [c.lng, c.lat] : [0, 0], zoom: m?.getZoom() ?? 0, pitch: m?.getPitch() ?? 0, bearing: m?.getBearing() ?? 0 };
  }
  getBounds(): MapBounds | null {
    const b = this.map?.getBounds();
    return b ? { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() } : null;
  }

  // ---- interaction (engine owns the plumbing; the host provides content + actions) ----
  onMoveEnd(cb: () => void): void { this.map?.on('moveend', cb); }
  /** The launch-assessment pin dropped on click / drag. */
  setAssessMarker(lnglat: [number, number] | null): void {
    const m = this.map;
    if (!m) return;
    this.assessMarker ??= new maplibregl.Marker({ color: '#f43f5e' });
    if (lnglat) this.assessMarker.setLngLat(lnglat).addTo(m);
    else this.assessMarker.remove();
  }
  /** Re-set the currently-shown hover tooltip HTML (used when a flight-path video frame loads async). */
  /**
   * A short, dismissable message anchored at the middle of the map. Reuses the tap popup because it
   * is already the one with a close button: a notice a visitor cannot dismiss is worse than none.
   */
  private notice(text: string): void {
    const m = this.map;
    if (!m || !this.tapPopup) return;
    const esc = text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
    this.hoverPopup?.remove();
    this.tapPopup.setLngLat(m.getCenter()).setHTML(`<div class="fp-tip-notice">${esc}</div>`).addTo(m);
  }

  updateHoverTip(html: string): void {
    if (this.map && this.hoverLngLat) this.hoverPopup?.setLngLat(this.hoverLngLat).setHTML(html).addTo(this.map);
  }

  setupInteraction<F extends { id: string }>(host: MapInteractionHost<F>): void {
    const m = this.map;
    if (!m) return;
    this.hoverPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: 'fp-tip', offset: 12, maxWidth: '260px' });
    this.tapPopup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, className: 'fp-tip fp-tip-tap', offset: 12, maxWidth: '280px' });
    m.getCanvas().style.cursor = 'grab';
    m.on('dragstart', () => { this.dragging = true; if (this.map) this.map.getCanvas().style.cursor = 'grabbing'; });
    m.on('dragend', () => { this.dragging = false; if (this.map) this.map.getCanvas().style.cursor = 'grab'; });
    m.on('mouseout', () => { if (this.hoverRaf) { cancelAnimationFrame(this.hoverRaf); this.hoverRaf = 0; } this.hoverPopup?.remove(); host.clearHighlight(); host.clearPathPoint(); });

    // Ctrl/Cmd-drag look-around: horizontal → rotate, vertical → tilt (2D↔3D). We roll our own instead of
    // MapLibre's dragRotate. On macOS Ctrl+click is a SECONDARY (button 2) mousedown + a contextmenu event,
    // so we accept button 2 (not just 0) and suppress the context menu, or Ctrl never rotates.
    m.dragRotate.disable();
    const canvas = m.getCanvas();
    let rot = false, rx = 0, ry = 0, rb = 0, rp = 0;
    const onDown = (e: MouseEvent) => {
      if (!this.map || !(e.ctrlKey || e.metaKey) || (e.button !== 0 && e.button !== 2)) return;
      rot = true; rx = e.clientX; ry = e.clientY; rb = this.map.getBearing(); rp = this.map.getPitch();
      this.map.dragPan.disable();
      e.preventDefault();
    };
    const onMove = (e: MouseEvent) => {
      if (!rot || !this.map) return;
      this.map.setBearing(rb + (e.clientX - rx) * 0.35);
      this.map.setPitch(Math.max(0, Math.min(75, rp - (e.clientY - ry) * 0.3)));
    };
    const onUp = () => { if (rot && this.map) { rot = false; this.map.dragPan.enable(); } };
    const onCtx = (e: MouseEvent) => { if (rot || e.ctrlKey) e.preventDefault(); };
    // Which device raised the click. MapLibre's `click` is the same event either way, and the two need
    // different answers: a mouse already has its hover tooltip up, a finger has never seen one.
    const onPointerDown = (e: PointerEvent) => { this.lastPointerType = e.pointerType; };
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('contextmenu', onCtx);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    canvas.setAttribute('aria-label', host.t('Map — press Enter to assess the centre point'));
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && this.map) {
        e.preventDefault();
        const c = this.map.getCenter();
        host.assess({ lat: c.lat, lon: c.lng });
      }
    };
    canvas.addEventListener('keydown', onKey);
    this.interactionCleanup = () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('contextmenu', onCtx);
      canvas.removeEventListener('keydown', onKey);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    /**
     * What is under this spot, resolved in ONE place for both a hover and a tap. Runs the priority
     * chain (photo marker → flight track → POI symbol → zones), performs the map-side effects
     * (outline, path marker, cursor) and returns the HTML to show, or null for "nothing here".
     *
     * It is one function because a hover and a tap ask the same question, and the tap answer is the
     * one nobody working at a desk would notice had drifted from the hover answer.
     */
    const resolveAt = (point: maplibregl.Point, lngLat: maplibregl.LngLat): string | null => {
      const map = this.map;
      if (!map) return null;
      const cursor = (c: string): void => { map.getCanvas().style.cursor = c; };
      const R = 9;
      const box: [[number, number], [number, number]] = [[point.x - R, point.y - R], [point.x + R, point.y + R]];
      // 0) A photo marker (a discrete point) wins over any line under the cursor → its rich tooltip.
      const phLayers = map.getLayer('photos-dot') ? ['photos-dot'] : [];
      const phHit = host.showFlights() && phLayers.length ? map.queryRenderedFeatures(box, { layers: phLayers }) : [];
      if (phHit.length) {
        cursor('pointer');
        host.clearPathPoint();
        host.clearHighlight();
        return host.photoTip(phHit[0].properties ?? {});
      }
      // 1) A flight track → outline it, mark the nearest point, show telemetry + the video frame there.
      const flHits = host.showFlights() && map.getLayer('flights-line') ? map.queryRenderedFeatures(box, { layers: ['flights-line'] }) : [];
      if (flHits.length) {
        let best: F | null = null, bestNear: readonly number[] | null = null;
        // The ground line is hit-tested; resolve to the flight's nearest sample to the cursor.
        {
          let bestD = Infinity;
          const seen = new Set<string>();
          for (const hit of flHits) {
            const id = String(hit.properties?.id ?? '');
            if (seen.has(id)) continue;
            seen.add(id);
            const f = host.flightById(id);
            const near = f ? host.nearestSample(f, lngLat.lng, lngLat.lat) : null;
            if (!f || !near) continue;
            const d = (near[0] - lngLat.lng) ** 2 + (near[1] - lngLat.lat) ** 2;
            if (d < bestD) { bestD = d; best = f; bestNear = near; }
          }
        }
        cursor('pointer');
        if (best && bestNear) {
          host.highlightFlight(best.id);
          host.setPathPoint(bestNear[0], bestNear[1], bestNear[3]); // [lon,lat,tSec,relAlt] → float at relAlt
          host.onHoverFrame(best, bestNear);
          return host.flightPathTip(best, bestNear);
        }
        const fp = flHits[0].properties ?? {};
        host.highlightFlight(String(fp.id ?? ''));
        host.clearPathPoint();
        return host.flightTip(fp);
      }
      host.clearPathPoint();
      // 2) A POI symbol → its name + kind (point marker, no area outline).
      const symLayers = SYMBOL_LAYERS.filter((l) => map.getLayer(l));
      const symHit = host.poi() && symLayers.length ? map.queryRenderedFeatures(point, { layers: symLayers }) : [];
      if (symHit.length) {
        cursor('pointer');
        host.clearHighlight();
        return host.symbolTip(symHit[0].properties ?? {});
      }
      // 3) Zones — the merged block (effective) or the stacked raw zones (direct).
      let sorted: TipRef[];
      let effHi: GeoJSON.Feature | null = null;
      if (host.effective()) {
        // The merged blocks carry only cls/category; the stacked detail comes from point-in-polygon
        // over the raw zones, so the block query only decides whether we are on one at all.
        const onBlock = map.queryRenderedFeatures(point, { layers: ['eff-nofly-fill', 'eff-notam-fill', 'eff-ok-fill'] }).length > 0;
        sorted = onBlock ? host.effInfosAt(lngLat.lat, lngLat.lng) : [];
        effHi = onBlock ? host.effComponentFeature(lngLat.lng, lngLat.lat) : null;
      } else {
        sorted = host.dedupeZones(map.queryRenderedFeatures(point, { layers: ZONE_LAYERS })).sort((a, b) => Number(a.properties?.area ?? 0) - Number(b.properties?.area ?? 0));
      }
      if (!sorted.length) {
        cursor(this.dragging ? 'grabbing' : 'grab');
        host.clearHighlight();
        return null;
      }
      cursor('pointer');
      if (host.effective()) host.highlightGeom(effHi);
      else host.highlightFeature(String(sorted[0].properties?.cls ?? ''), String(sorted[0].properties?.id ?? ''));
      return host.zoneTip(sorted);
    };

    // One map-level hover makes EVERY annotation interactive: outline what's under the cursor + show its
    // details. Coalesced to one run per animation frame; sub-2px jitter skipped (a raw mousemove fires
    // ~120×/s).
    const runMapHover = () => {
      this.hoverRaf = 0;
      const e = this.hoverEv;
      const map = this.map;
      if (!e || !map) return;
      if (!this.ready) return; // layers aren't built until 'load' — hovering during the ~3 s load would
      // queryRenderedFeatures against layers (eff-nofly-fill, …) that don't exist yet and spam errors.
      const html = resolveAt(e.point, e.lngLat);
      if (html == null) { this.hoverPopup?.remove(); return; }
      this.hoverLngLat = e.lngLat;
      this.hoverPopup?.setLngLat(e.lngLat).setHTML(html).addTo(map);
    };
    m.on('mousemove', (e) => {
      if (Math.abs(e.point.x - this.lastHx) < 2 && Math.abs(e.point.y - this.lastHy) < 2) return; // jitter
      this.lastHx = e.point.x;
      this.lastHy = e.point.y;
      this.hoverEv = e;
      if (!this.hoverRaf) this.hoverRaf = requestAnimationFrame(runMapHover);
    });

    // Click anywhere — even inside a zone — assesses that point; clicking your own flight opens its detail
    // card. Deferred so a double-click (zoom) cancels it instead of dropping a pin.
    m.on('click', (e) => {
      const pt = e.point;
      const ll = e.lngLat;
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => {
        const map = this.map;
        if (!map) return;
        const phc = map.getLayer('photos-dot') ? map.queryRenderedFeatures(pt, { layers: ['photos-dot'] }) : [];
        if (phc.length) { host.openPhoto(String(phc[0].properties?.id ?? '')); return; }
        const fd = map.getLayer('flights-dot') ? map.queryRenderedFeatures(pt, { layers: ['flights-dot'] }) : [];
        if (fd.length) { host.openFlightDetail(String(fd[0].properties?.id ?? ''), { x: pt.x, y: pt.y }); return; }
        const R = 8;
        const box: [[number, number], [number, number]] = [[pt.x - R, pt.y - R], [pt.x + R, pt.y + R]];
        const pathLayers = map.getLayer('flights-line') ? ['flights-line'] : [];
        const ph = host.showFlights() && pathLayers.length ? map.queryRenderedFeatures(box, { layers: pathLayers }) : [];
        if (ph.length) {
          let best: F | null = null, bestD = Infinity;
          const seen = new Set<string>();
          for (const hit of ph) {
            const id = String(hit.properties?.id ?? '');
            if (seen.has(id)) continue;
            seen.add(id);
            const f = host.flightById(id);
            const near = f ? host.nearestSample(f, ll.lng, ll.lat) : null;
            if (!f || !near) continue;
            const d = (near[0] - ll.lng) ** 2 + (near[1] - ll.lat) ** 2;
            if (d < bestD) { bestD = d; best = f; }
          }
          if (best) { host.openFlightDetail(best.id, { x: pt.x, y: pt.y }); return; }
        }
        // A finger raises no real hover, so every readout above was effectively mouse-only. What a
        // tap DID produce was the browser's one compatibility mousemove, which opens the hover
        // popup — a cursor-sized card with no close button and no mouseout ever coming to remove it,
        // so it sat there until the next tap somewhere else. The tap popup replaces it: same
        // content, readable size, dismissable.
        if (this.lastPointerType === 'touch') {
          const html = resolveAt(pt, ll);
          this.hoverPopup?.remove();
          if (html != null) this.tapPopup?.setLngLat(ll).setHTML(html).addTo(map);
          else this.tapPopup?.remove(); // tapping bare ground dismisses — the idiom people have
        }
        host.assess({ lat: ll.lat, lon: ll.lng });
      }, 240);
    });
    m.on('dblclick', () => clearTimeout(this.clickTimer));
  }

  // ---- 3D / terrain (capability: threeD/terrain) ----
  // 2D/3D is driven by PITCH: tilting past a threshold turns the 3D presentation (terrain relief + buildings)
  // ON, flattening turns it OFF — so a hand-tilt and the explicit 2D/3D button converge on the same state.
  // `set3D` only animates the camera; `apply3D` (run by the pitch watcher) flips the layers.
  set3D(on: boolean): void {
    this.map?.easeTo(on ? { pitch: 62, duration: 700 } : { pitch: 0, bearing: 0, duration: 700 });
  }
  private terrainPending = false; // a deferred setTerrain is queued (see apply3D)
  private apply3D(on: boolean): void {
    const m = this.map;
    if (!m) return;
    // Buildings + routes flip instantly (cheap). Terrain is the expensive part and is handled below.
    if (m.getLayer('buildings-3d')) m.setLayoutProperty('buildings-3d', 'visibility', on ? 'visible' : 'none');
    if (!on) {
      m.setTerrain(null); // flattening is cheap — do it now
      this.updateDeck();
      return;
    }
    // Enabling terrain rebuilds a full render-to-texture mesh AND decodes the visible DEM tiles. Doing that
    // synchronously while the pitch ease is still animating (or on a fresh restored-3D page load) compounded
    // into a multi-second main-thread stall — the "switching to 3D freezes the whole app" bug. Defer it until
    // the camera goes idle so the tilt stays smooth and the relief fades in a beat later. A timeout backstops
    // the case where 'idle' is slow to fire under a heavy tile load; a shared flag makes the two paths race-safe.
    if (!this.terrainPending) {
      this.terrainPending = true;
      const apply = (): void => {
        if (!this.terrainPending) return; // the idle + timeout race — only the first wins
        this.terrainPending = false;
        if (!this.map || !this.is3d) return; // flattened again before it fired → nothing to do
        // Bracket the terrain build: setTerrain() itself returns fast, but the mesh rebuild + DEM decode
        // happen in MapLibre's next render frames — showing up as a long task with NO app-code attribution.
        // Timing setTerrain→idle measures that window directly, so we can confirm terrain IS the ~1 s stall.
        const tt = plog('setTerrain: applying (mesh build starts)');
        this.map.setTerrain({ source: 'dem', exaggeration: 1.3 });
        // Terrain elevation reads null until the DEM tiles arrive → re-anchor the routes once they do,
        // otherwise a plateau launch (rel_alt treated as MSL) draws the path underground. A single idle isn't
        // enough: the tile under a given home may still be undecoded at that idle, so anchor() reports "pending"
        // and we retry (cheap — home queries only) until every home resolves or we give up.
        this.map.once('idle', () => {
          plog('setTerrain: idle (mesh built + tiles in)', tt);
          const reanchor = (tries: number): void => {
            const pending = this.deck?.anchor() ?? false;
            if (pending && tries > 0 && this.is3d) this.later(() => reanchor(tries - 1), 300);
          };
          reanchor(8);
        });
      };
      m.once('idle', apply);
      this.later(apply, 900);
    }
    this.updateDeck(); // 3D routes appear with the tilt (they don't need terrain)
  }
  /** Watch pitch and flip the 3D presentation as it crosses the tilt threshold (hysteresis: enter ≥12°, exit
   *  <8° — avoids flicker at the boundary). `cb` lets the host mirror the state in its 2D/3D button. */
  on3DChange(cb: (on: boolean) => void): void {
    const m = this.map;
    if (!m) return;
    const check = (): void => {
      const p = m.getPitch();
      if (!this.is3d && p >= 12) { this.is3d = true; this.apply3D(true); cb(true); }
      else if (this.is3d && p < 8) { this.is3d = false; this.apply3D(false); cb(false); }
    };
    m.on('pitch', check);
    check(); // sync an initial (e.g. restored) pitched view
  }

  destroy(): void {
    if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);
    this.hoverRaf = 0;
    clearTimeout(this.clickTimer);
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.interactionCleanup?.();
    this.interactionCleanup = undefined;
    this.hoverPopup?.remove();
    this.tapPopup?.remove();
    this.deck?.dispose();
    this.deck = undefined;
    this.assessMarker?.remove();
    for (const mk of this.windMarkers) mk.remove();
    this.windMarkers = [];
    this.wxPopup?.remove();
    this.map?.remove();
    this.map = undefined;
    this.readyCbs = [];
    this.ready = false;
  }

  // ---- weather (capability: weather) — field layers + rotatable DOM wind markers with a hover popup ----
  setWeatherField(temp: GeoJSON.FeatureCollection | null, precip: GeoJSON.FeatureCollection | null, windFlow: GeoJSON.FeatureCollection | null): void {
    this.src('wx-temp')?.setData(temp ?? MapLibreEngine.EMPTY_FC);
    this.src('wx-precip')?.setData(precip ?? MapLibreEngine.EMPTY_FC);
    this.src('wx-wind')?.setData(windFlow ?? MapLibreEngine.EMPTY_FC);
  }
  /** The bold sampled-point markers. The panel builds each marker's DOM (`el`) + tooltip HTML (`tip`);
   *  the engine owns the maplibregl.Marker + the hover popup. Replaces the previous set. */
  setWindMarkers(markers: Array<{ lnglat: [number, number]; el: HTMLElement; tip: string }>): void {
    for (const mk of this.windMarkers) mk.remove();
    this.windMarkers = [];
    const m = this.map;
    if (!m) return;
    this.wxPopup ??= new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: 'fp-tip fp-wxtip-pop', offset: 16, maxWidth: '280px' });
    for (const { lnglat, el, tip } of markers) {
      el.addEventListener('mouseenter', () => { if (this.map) this.wxPopup!.setLngLat(lnglat).setHTML(tip).addTo(this.map); });
      el.addEventListener('mouseleave', () => this.wxPopup?.remove());
      this.windMarkers.push(new maplibregl.Marker({ element: el }).setLngLat(lnglat).addTo(m));
    }
  }

  // Render each category emoji to a canvas + register it as a MAP IMAGE, so the exact emoji the filter
  // chips show is what appears on the map — one source of truth, no sprite/emoji drift. Added once; the
  // basemap toggle only flips layer visibility (no setStyle), so the images persist.
  /**
   * A diagonal hatch for the no-fly fills, so "may I fly here" is not encoded by hue alone.
   *
   * Red against green is the worst possible pair for the commonest colour deficiency: to a
   * deuteranope the two fills sit within a few percent of each other, and this map's whole purpose
   * is telling them apart. A stripe texture survives any colour vision, greyscale printing and a
   * washed-out screen in daylight — which is the condition someone checks this in.
   *
   * Dark and semi-transparent so it reads as a texture ON the red rather than a second colour, and
   * so it holds against both the light street basemap and the dark satellite one.
   */
  private ensureHatch(map: maplibregl.Map): void {
    if (map.hasImage('nofly-hatch')) return;
    const size = 16; // repeats seamlessly: the stripes are drawn at 45° with a period of size/2
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    // 0.72 at fill-opacity 1, not 0.62 at 0.5. The first version multiplied out to an effective
    // alpha of 0.31, which reads at country zoom — where a red patch is small and the texture
    // catches the eye — and all but vanishes over a screen-filling red area at street zoom, which
    // is where someone standing at the spot actually looks. Measured against the Leaflet fallback
    // drawing the same mark at 0.75, side by side over the same satellite imagery.
    ctx.strokeStyle = 'rgba(9, 18, 32, 0.72)';
    ctx.lineWidth = 3;
    ctx.lineCap = 'square';
    // Three passes offset by one period each so the diagonal continues across the tile edges.
    for (const off of [-size, 0, size]) {
      ctx.beginPath();
      ctx.moveTo(off - 1, size + 1);
      ctx.lineTo(off + size + 1, -1);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(off + size / 2 - 1, size + 1);
      ctx.lineTo(off + size + size / 2 + 1, -1);
      ctx.stroke();
    }
    map.addImage('nofly-hatch', ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
  }

  private ensureEmojiIcons(map: maplibregl.Map): void {
    const emojis = new Set<string>(CATEGORIES.map((c) => c.icon));
    emojis.add(HELIPORT_ICON);
    const size = 48;
    for (const emoji of emojis) {
      if (map.hasImage(emoji)) continue;
      const c = document.createElement('canvas');
      c.width = c.height = size;
      const ctx = c.getContext('2d');
      if (!ctx) continue;
      ctx.font = `${Math.round(size * 0.78)}px "Apple Color Emoji","Noto Color Emoji","Segoe UI Emoji",sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, size / 2, size / 2 + size * 0.06);
      map.addImage(emoji, ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
    }
  }

  // A white rounded tile with a camera glyph — the photo marker. Distinct in shape from the coloured
  // flight dots so the two kinds never blur together. Drawn once; persists across basemap toggles.
  private ensurePhotoIcon(map: maplibregl.Map): void {
    if (map.hasImage('photo-marker')) return;
    const size = 44;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const pad = 4;
    const w = size - 2 * pad;
    ctx.beginPath();
    // roundRect is unavailable on older WebKit/WKWebView (the tauri build) — fall back to a plain rect so
    // a missing method never throws out of buildLayers() and aborts the rest of the layer stack.
    if (typeof ctx.roundRect === 'function') ctx.roundRect(pad, pad, w, w, 10);
    else ctx.rect(pad, pad, w, w);
    ctx.fillStyle = '#f8fafc';
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#0b1220';
    ctx.stroke();
    ctx.font = `${Math.round(size * 0.5)}px "Apple Color Emoji","Noto Color Emoji","Segoe UI Emoji",sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('📷', size / 2, size / 2 + size * 0.04);
    map.addImage('photo-marker', ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
  }

  // The full layer stack — all sources start EMPTY; the panel pushes data via setData (still through
  // `raw` until those setters migrate here). Order = paint order (bottom → top).
  private buildLayers(map: maplibregl.Map): void {
    map.resize();
    const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
    // Terrain from the DEM (under the zones) — hidden until toggled. Elevation colours give a hypsometric
    // tint (low=green → high=white); hillshade adds relief so ridges/valleys read.
    map.addLayer({
      id: 'color-relief',
      type: 'color-relief',
      source: 'dem',
      layout: { visibility: 'none' },
      paint: {
        'color-relief-color': [
          'interpolate', ['linear'], ['elevation'],
          0, '#1a6e3c', 300, '#4a9d4a', 700, '#c9d66b', 1200, '#e6c34d',
          1800, '#d99a3f', 2500, '#a06a43', 3200, '#8a6a5a', 4000, '#e8e2dc', 5000, '#ffffff',
        ],
        'color-relief-opacity': 0.6,
      },
    });
    map.addLayer({ id: 'hillshade', type: 'hillshade', source: 'dem', layout: { visibility: 'none' }, paint: { 'hillshade-exaggeration': 0.45 } });
    // Contour lines (client-side from the DEM, via maplibre-contour). Hidden until toggled.
    map.addSource('contours', {
      type: 'vector',
      // Coarser intervals at common zooms → far fewer marching-squares segments on the main thread; the
      // dense 50 m interval only kicks in when zoomed right in.
      tiles: [demSource.contourProtocolUrl({ thresholds: { 9: [500, 1000], 11: [200, 1000], 13: [100, 500], 15: [50, 250] }, elevationKey: 'ele', levelKey: 'level', contourLayer: 'contours' })],
      maxzoom: 15,
    });
    // Dark casing under a BRIGHT elevation-coloured line (topographic palette) so contours read on BOTH
    // satellite and terrain-coloured backgrounds. Colour by `ele` — same ramp as the elevation relief.
    const CONTOUR_COLOR: ExpressionSpecification = [
      'interpolate', ['linear'], ['get', 'ele'],
      0, '#35d07f', 400, '#a8e05f', 900, '#ffe14d', 1500, '#ffb020', 2200, '#ff7a3d', 3000, '#e8caa4', 4000, '#ffffff',
    ];
    map.addLayer({ id: 'contour-casing', type: 'line', source: 'contours', 'source-layer': 'contours', layout: { visibility: 'none', 'line-join': 'round' }, paint: { 'line-color': 'rgba(15,20,30,0.6)', 'line-width': ['match', ['get', 'level'], 1, 4.5, 2.6] } });
    map.addLayer({ id: 'contour-lines', type: 'line', source: 'contours', 'source-layer': 'contours', layout: { visibility: 'none', 'line-join': 'round' }, paint: { 'line-color': CONTOUR_COLOR, 'line-width': ['match', ['get', 'level'], 1, 2.4, 1.2], 'line-opacity': ['match', ['get', 'level'], 1, 1, 0.85] } });
    map.addLayer({ id: 'contour-labels', type: 'symbol', source: 'contours', 'source-layer': 'contours', filter: ['>', ['get', 'level'], 0], layout: { visibility: 'none', 'symbol-placement': 'line', 'text-field': ['concat', ['to-string', ['get', 'ele']], ' m'], 'text-font': ['Noto Sans Regular'], 'text-size': 10 }, paint: { 'text-color': '#4a3527', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 } });
    // Higher `tolerance` = more geometry simplification = far fewer vertices to render each zoom frame
    // (the SHGM dataset is ~9k detailed polygons). buffer=64 trims per-tile overhead too.
    for (const id of ['zones-nofly', 'zones-ok', 'notams']) map.addSource(id, { type: 'geojson', data: empty, tolerance: 2, buffer: 64 });
    map.addLayer({ id: 'zones-ok-fill', type: 'fill', source: 'zones-ok', paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.16, 'fill-antialias': false } });
    map.addLayer({ id: 'zones-ok-line', type: 'line', source: 'zones-ok', paint: { 'line-color': '#16a34a', 'line-width': 1 } });
    map.addLayer({ id: 'notam-fill', type: 'fill', source: 'notams', paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.2, 'fill-antialias': false } });
    map.addLayer({ id: 'notam-line', type: 'line', source: 'notams', paint: { 'line-color': '#d97706', 'line-width': 1 } });
    map.addLayer({ id: 'zones-nofly-fill', type: 'fill', source: 'zones-nofly', paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.42, 'fill-antialias': false } });
    // Dark casing UNDER the red outline: a small no-fly pocket then reads as a red ring on any basemap
    // (satellite, terrain, or a large green fill) instead of thinning into a faint hairline that vanishes.
    this.ensureHatch(map);
    map.addLayer({ id: 'zones-nofly-hatch', type: 'fill', source: 'zones-nofly', paint: { 'fill-pattern': 'nofly-hatch', 'fill-opacity': 1 } });
    map.addLayer({ id: 'zones-nofly-casing', type: 'line', source: 'zones-nofly', layout: { 'line-join': 'round' }, paint: { 'line-color': 'rgba(11,18,32,0.6)', 'line-width': NOFLY_CASE_W } });
    map.addLayer({ id: 'zones-nofly-line', type: 'line', source: 'zones-nofly', layout: { 'line-join': 'round' }, paint: { 'line-color': '#dc2626', 'line-width': NOFLY_LINE_W } });
    // Effective (geometrically merged) zones — one clean block per class, stricter class clips looser.
    // Hidden until the effective toggle is on; raw layers hide then so overlaps don't double-render.
    for (const id of ['eff-ok', 'eff-notam', 'eff-nofly']) map.addSource(id, { type: 'geojson', data: empty, tolerance: 2, buffer: 64 });
    map.addLayer({ id: 'eff-ok-fill', type: 'fill', source: 'eff-ok', layout: { visibility: 'none' }, paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.2, 'fill-antialias': false } });
    map.addLayer({ id: 'eff-ok-line', type: 'line', source: 'eff-ok', layout: { visibility: 'none' }, paint: { 'line-color': '#16a34a', 'line-width': 1.2 } });
    map.addLayer({ id: 'eff-notam-fill', type: 'fill', source: 'eff-notam', layout: { visibility: 'none' }, paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.26, 'fill-antialias': false } });
    map.addLayer({ id: 'eff-notam-line', type: 'line', source: 'eff-notam', layout: { visibility: 'none' }, paint: { 'line-color': '#d97706', 'line-width': 1.2 } });
    map.addLayer({ id: 'eff-nofly-fill', type: 'fill', source: 'eff-nofly', layout: { visibility: 'none' }, paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.48, 'fill-antialias': false } });
    map.addLayer({ id: 'eff-nofly-hatch', type: 'fill', source: 'eff-nofly', layout: { visibility: 'none' }, paint: { 'fill-pattern': 'nofly-hatch', 'fill-opacity': 1 } });
    // Effective mode is fills-only (outlining every merged piece would seam) EXCEPT tiny pockets at overview
    // zoom, where the cased ring rescues a red carve-out that would otherwise sub-pixel away in a big green
    // area. Gated to z<12 (fades by then) so a zoomed-in, already-visible zone is never given a special edge.
    map.addLayer({ id: 'eff-nofly-casing', type: 'line', source: 'eff-nofly', filter: SMALL_NOFLY, maxzoom: 12, layout: { visibility: 'none', 'line-join': 'round' }, paint: { 'line-color': 'rgba(11,18,32,0.6)', 'line-width': NOFLY_CASE_W, 'line-opacity': EFF_RING_OPACITY } });
    map.addLayer({ id: 'eff-nofly-line', type: 'line', source: 'eff-nofly', filter: SMALL_NOFLY, maxzoom: 12, layout: { visibility: 'none', 'line-join': 'round' }, paint: { 'line-color': '#dc2626', 'line-width': NOFLY_LINE_W, 'line-opacity': EFF_RING_OPACITY } });
    // Weather "fields" — smoothly-blended circle heat fields. Temperature runs a cold→hot ramp; precip
    // paints blue only where it's actually raining. Wind stays as rotatable arrow markers (DOM, above).
    for (const id of ['wx-temp', 'wx-precip', 'wx-wind']) map.addSource(id, { type: 'geojson', data: empty });
    map.addLayer({ id: 'wx-temp-field', type: 'circle', source: 'wx-temp', paint: { 'circle-radius': 44, 'circle-color': TEMP_RAMP, 'circle-opacity': 0.36, 'circle-blur': 1.1 } });
    map.addLayer({
      id: 'wx-precip-field',
      type: 'circle',
      source: 'wx-precip',
      paint: { 'circle-radius': 42, 'circle-color': '#3b82f6', 'circle-opacity': ['interpolate', ['linear'], ['get', 'precip'], 0, 0.16, 1, 0.34, 4, 0.5, 10, 0.62], 'circle-blur': 1.1 },
    });
    map.addLayer({
      id: 'wx-wind-flow',
      type: 'symbol',
      source: 'wx-wind',
      layout: { 'text-field': '↑', 'text-font': ['Noto Sans Bold'], 'text-rotate': ['get', 'to'], 'text-rotation-alignment': 'map', 'text-size': ['interpolate', ['linear'], ['get', 'wind'], 0, 14, 15, 30], 'text-allow-overlap': true, 'text-ignore-placement': true },
      paint: { 'text-color': ['step', ['get', 'wind'], '#22c55e', 5, '#eab308', 8, '#f97316', 12, '#ef4444'], 'text-opacity': 0.5, 'text-halo-color': 'rgba(0,0,0,0.55)', 'text-halo-width': 1 },
    });
    // Hover highlight — a bright outline + faint fill over the specific zone/flight under the cursor.
    map.addSource('highlight', { type: 'geojson', data: empty });
    map.addLayer({ id: 'highlight-fill', type: 'fill', source: 'highlight', paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.16 } });
    map.addLayer({ id: 'highlight-glow', type: 'line', source: 'highlight', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 9, 'line-opacity': 0.35, 'line-blur': 3 } });
    map.addLayer({ id: 'highlight-line', type: 'line', source: 'highlight', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 4.5, 'line-opacity': 1 } });
    // The user's own flight tracks — a DISTINCT colour per flight so overlapping paths never blur.
    map.addSource('flights', { type: 'geojson', data: empty });
    map.addSource('flights-dots', { type: 'geojson', data: empty });
    map.addLayer({ id: 'flights-casing', type: 'line', source: 'flights', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#0b1220', 'line-width': 6, 'line-opacity': 0.6 } });
    map.addLayer({ id: 'flights-line', type: 'line', source: 'flights', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': ['get', 'color'], 'line-width': 3.5, 'line-opacity': 0.95 } });
    map.addLayer({ id: 'flights-dot', type: 'circle', source: 'flights-dots', paint: { 'circle-radius': 5, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#0b1220', 'circle-stroke-width': 2 } });
    map.addLayer({
      id: 'flights-label',
      type: 'symbol',
      source: 'flights',
      layout: { 'symbol-placement': 'line-center', 'text-field': ['concat', ['get', 'title'], '\n', ['get', 'date']], 'text-font': ['Noto Sans Bold'], 'text-size': 12, 'text-max-width': 12, 'text-allow-overlap': false },
      paint: { 'text-color': ['get', 'color'], 'text-halo-color': '#0b1220', 'text-halo-width': 2, 'text-halo-blur': 0.5 },
    });
    // The airborne route is drawn as a CLEAN deck.gl line at real altitude (see FlightDeck) — fill-extrusion
    // only makes chunky stair-stepped boxes. The overlay is created here but loads deck.gl lazily on first 3D.
    this.deck = new FlightDeck(map);
    // Photos — one camera marker per located capture (§2.5). A symbol (not a coloured dot) so a photo is
    // never mistaken for a flight take-off. Archived (moved-out) captures render dimmed.
    map.addSource('photos', { type: 'geojson', data: empty });
    map.addLayer({
      id: 'photos-dot',
      type: 'symbol',
      source: 'photos',
      layout: { 'icon-image': 'photo-marker', 'icon-size': 0.5, 'icon-allow-overlap': true },
      paint: { 'icon-opacity': ['case', ['==', ['get', 'archived'], true], 0.45, 1] },
    });
    // Marker for the exact point being hovered ALONG a flight path (with its telemetry + video frame). The
    // floating bead (fill-extrusion at the sample's rel_alt) rides the AIRBORNE path in a pitched view; the
    // ground circle stays as its shadow. Bead added first so the crisp circle draws on top when top-down.
    map.addSource('path-point-air', { type: 'geojson', data: empty });
    map.addLayer({ id: 'path-point-air', type: 'fill-extrusion', source: 'path-point-air', paint: { 'fill-extrusion-color': '#fde047', 'fill-extrusion-base': ['get', 'base'], 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-opacity': 0.95 } });
    map.addSource('path-point', { type: 'geojson', data: empty });
    map.addLayer({ id: 'path-point', type: 'circle', source: 'path-point', paint: { 'circle-radius': 6, 'circle-color': '#fde047', 'circle-stroke-color': '#0b1220', 'circle-stroke-width': 2 } });
    // Playback marker — where the detail-card video currently is, moving along the flight as it plays.
    map.addSource('play-point-air', { type: 'geojson', data: empty });
    map.addLayer({ id: 'play-point-air', type: 'fill-extrusion', source: 'play-point-air', paint: { 'fill-extrusion-color': '#22d3ee', 'fill-extrusion-base': ['get', 'base'], 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-opacity': 0.95 } });
    map.addSource('play-point', { type: 'geojson', data: empty });
    map.addLayer({ id: 'play-point-halo', type: 'circle', source: 'play-point', paint: { 'circle-radius': 12, 'circle-color': '#22d3ee', 'circle-opacity': 0.28 } });
    map.addLayer({ id: 'play-point', type: 'circle', source: 'play-point', paint: { 'circle-radius': 6, 'circle-color': '#22d3ee', 'circle-stroke-color': '#0b1220', 'circle-stroke-width': 2.5 } });
    // 3D buildings (OpenFreeMap `building` layer, OSM heights) — hidden until the 3D toggle is on.
    map.addLayer({
      id: 'buildings-3d',
      type: 'fill-extrusion',
      source: 'openfreemap',
      'source-layer': 'building',
      minzoom: 14,
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-color': '#9aa4b2',
        'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 6],
        'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], ['get', 'min_height'], 0],
        'fill-extrusion-opacity': 0.85,
      },
    });
    // Vector labels ride ABOVE the zone overlays — they stay UPRIGHT when the map rotates and we control
    // size/colour/halo for readability over any basemap.
    const NAME: ExpressionSpecification = ['coalesce', ['get', 'name:tr'], ['get', 'name'], ['get', 'name:latin']];
    const HALO = { 'text-color': '#ffffff', 'text-halo-color': '#12151b', 'text-halo-width': 1.9, 'text-halo-blur': 0.3 };
    map.addLayer({ id: 'lbl-water', type: 'symbol', source: 'openfreemap', 'source-layer': 'water_name', layout: { 'text-field': NAME, 'text-font': ['Noto Sans Regular'], 'text-size': 12, 'text-max-width': 6 }, paint: { 'text-color': '#cbe8ff', 'text-halo-color': '#0b2036', 'text-halo-width': 1.4 } });
    map.addLayer({ id: 'lbl-road', type: 'symbol', source: 'openfreemap', 'source-layer': 'transportation_name', minzoom: 12, layout: { 'text-field': NAME, 'text-font': ['Noto Sans Regular'], 'text-size': 11.5, 'symbol-placement': 'line', 'text-max-angle': 40 }, paint: { ...HALO, 'text-halo-width': 1.5 } });
    // Place labels split into importance BUCKETS, each with its own minzoom, so a name only appears at a
    // zoom where it makes sense: country/city always, districts (town) from z7, villages/neighbourhoods from
    // z10.5. (MapLibre filters can't read `zoom`, so per-class zoom-gating HAS to be separate layers.) Within
    // a bucket, `symbol-sort-key: rank` lets the more important place win the collision, so decluttering
    // keeps the label that matters instead of whichever happened to render first.
    const PLACE_LAYOUT = { 'text-field': NAME, 'text-font': ['Noto Sans Bold'] as unknown as string[], 'symbol-sort-key': ['get', 'rank'] as unknown as number, 'text-max-width': 7, 'text-padding': 4 };
    // Country + continent: a handful, always on — they orient the whole view. Dimmer so settlements lead.
    map.addLayer({
      id: 'lbl-place-base', type: 'symbol', source: 'openfreemap', 'source-layer': 'place',
      filter: ['in', ['get', 'class'], ['literal', ['country', 'continent']]] as ExpressionSpecification,
      layout: { ...PLACE_LAYOUT, 'text-size': ['interpolate', ['linear'], ['zoom'], 3, ['match', ['get', 'class'], 'country', 13, 11], 8, ['match', ['get', 'class'], 'country', 17, 14]] },
      paint: { ...HALO, 'text-color': '#e2e8f0', 'text-opacity': 0.85 },
    });
    // İl (province): cities + states — the important names, visible at overview zoom.
    map.addLayer({
      id: 'lbl-place-il', type: 'symbol', source: 'openfreemap', 'source-layer': 'place',
      filter: ['in', ['get', 'class'], ['literal', ['city', 'state']]] as ExpressionSpecification,
      layout: { ...PLACE_LAYOUT, 'text-size': ['interpolate', ['linear'], ['zoom'], 5, ['match', ['get', 'class'], 'city', 14, 11], 11, ['match', ['get', 'class'], 'city', 23, 17]] },
      paint: HALO,
    });
    // İlçe (district): towns — only once you've zoomed in enough that they aren't a wall of text.
    map.addLayer({
      id: 'lbl-place-ilce', type: 'symbol', source: 'openfreemap', 'source-layer': 'place', minzoom: 7,
      filter: ['==', ['get', 'class'], 'town'] as ExpressionSpecification,
      layout: { ...PLACE_LAYOUT, 'text-size': ['interpolate', ['linear'], ['zoom'], 7, 11, 12, 16] },
      paint: HALO,
    });
    // Köy (village) + neighbourhoods: the densest layer — held back to z10.5 so the country view stays clean.
    map.addLayer({
      id: 'lbl-place-koy', type: 'symbol', source: 'openfreemap', 'source-layer': 'place', minzoom: 10.5,
      filter: ['in', ['get', 'class'], ['literal', ['village', 'hamlet', 'isolated_dwelling', 'suburb', 'quarter', 'neighbourhood']]] as ExpressionSpecification,
      layout: { ...PLACE_LAYOUT, 'text-size': ['interpolate', ['linear'], ['zoom'], 10.5, 10, 14, 13.5] },
      paint: { ...HALO, 'text-color': '#e8edf4' },
    });
    // Register the shared emoji icons before any symbol layer references them.
    this.ensureEmojiIcons(map);
    this.ensurePhotoIcon(map);
    // Drone-relevant POIs (airports / heliports / military airfields). Default ON — the things you MUST
    // know before flying. Same emoji scheme as the zone-category symbols + the filter chips.
    map.addLayer({
      id: 'poi-aero',
      type: 'symbol',
      source: 'openfreemap',
      'source-layer': 'aerodrome_label',
      layout: {
        'icon-image': ['match', ['get', 'class'], 'military', categoryIcon('military'), 'heliport', HELIPORT_ICON, categoryIcon('airport')],
        'icon-size': 0.75,
        'icon-allow-overlap': true,
        'text-field': NAME,
        'text-font': ['Noto Sans Bold'],
        'text-size': 11,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: { 'text-color': '#ffe08a', 'text-halo-color': '#12151b', 'text-halo-width': 1.6 },
    });
    // Drone-relevant SITE symbols derived from the SHGM zones themselves (military / dam / SİT / nuclear /
    // prison / energy …). Populated by the panel via the poi-zone source.
    map.addSource('poi-zone', { type: 'geojson', data: empty });
    map.addLayer({
      id: 'poi-zone',
      type: 'symbol',
      source: 'poi-zone',
      layout: {
        'icon-image': ['get', 'sym'],
        // Smaller when zoomed out so a dense country view reads as texture rather than clutter,
        // full size once the markers are individually meaningful.
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 9, 0.75],
        'icon-allow-overlap': false,
        'text-field': poiNameField(),
        'text-font': ['Noto Sans Bold'],
        'text-size': 10,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-optional': true,
        'text-max-width': 8,
      },
      paint: { 'text-color': '#e0f2fe', 'text-halo-color': '#12151b', 'text-halo-width': 1.5 },
    });
  }
}
